import React, { useMemo, useCallback, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  ReactFlowProvider,
  useReactFlow,
  Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { toPng, toSvg } from 'html-to-image';
import { OdooOrderLine, ProductAssignment, PARENT_CATEGORIES, CHILD_CATEGORIES, DCI_CATEGORY, LEFT_SIDE_CATEGORIES, RIGHT_SIDE_CATEGORIES, FWAAS_CATEGORY, filterAssignableLines } from './types';
import { DataCenterItem } from '../ResourceManager/Hardware/request/request';

// Export handle type for parent component to call export
export interface ServiceArchitectureGraphHandle {
  exportDiagram: (format: 'svg' | 'png') => Promise<void>;
  getImageDataUrl: (format: 'png') => Promise<string | null>;
}

export interface ServiceArchitectureGraphProps {
  orderLines: OdooOrderLine[];
  assignments: ProductAssignment[];
  parentDatacenterMap?: Record<number, string>; // parentLineId -> datacenterId
  datacenters?: DataCenterItem[];
  dciPortAssignments?: Record<number, { portA: number | null; portB: number | null }>; // dciLineId -> { portA, portB }
  sdwanHubRoles?: Record<number, 'primary' | 'secondary'>; // hubLineId -> role
  sdwanBranchPrimaryHub?: Record<number, number>; // branchLineId -> primaryHubLineId
}

// Unique color palette for each parent INSTANCE - 50 distinct colors
const uniqueParentColors: Array<{ bg: string; border: string; text: string }> = [
  // Primary colors - very distinct
  { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' }, // 1. Amber
  { bg: '#D1FAE5', border: '#10B981', text: '#065F46' }, // 2. Emerald
  { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' }, // 3. Blue
  { bg: '#FCE7F3', border: '#EC4899', text: '#9D174D' }, // 4. Pink
  { bg: '#E0E7FF', border: '#6366F1', text: '#3730A3' }, // 5. Indigo
  { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B' }, // 6. Red
  { bg: '#F3E8FF', border: '#A855F7', text: '#6B21A8' }, // 7. Purple
  { bg: '#CCFBF1', border: '#14B8A6', text: '#115E59' }, // 8. Teal
  { bg: '#CFFAFE', border: '#06B6D4', text: '#155E75' }, // 9. Cyan
  { bg: '#FFE4E6', border: '#F43F5E', text: '#881337' }, // 10. Rose
  { bg: '#ECFCCB', border: '#84CC16', text: '#3F6212' }, // 11. Lime
  { bg: '#FEF9C3', border: '#EAB308', text: '#713F12' }, // 12. Yellow
  { bg: '#FED7AA', border: '#EA580C', text: '#9A3412' }, // 13. Orange
  { bg: '#BBF7D0', border: '#22C55E', text: '#166534' }, // 14. Green
  { bg: '#BAE6FD', border: '#0284C7', text: '#075985' }, // 15. Sky
  { bg: '#DDD6FE', border: '#7C3AED', text: '#5B21B6' }, // 16. Violet
  { bg: '#FBCFE8', border: '#D946EF', text: '#86198F' }, // 17. Fuchsia
  // Darker variants
  { bg: '#FDE68A', border: '#D97706', text: '#78350F' }, // 18. Dark Amber
  { bg: '#A7F3D0', border: '#059669', text: '#064E3B' }, // 19. Dark Emerald
  { bg: '#93C5FD', border: '#2563EB', text: '#1E3A8A' }, // 20. Dark Blue
  { bg: '#F9A8D4', border: '#DB2777', text: '#831843' }, // 21. Dark Pink
  { bg: '#C7D2FE', border: '#4F46E5', text: '#312E81' }, // 22. Dark Indigo
  { bg: '#FECACA', border: '#DC2626', text: '#7F1D1D' }, // 23. Dark Red
  { bg: '#E9D5FF', border: '#9333EA', text: '#581C87' }, // 24. Dark Purple
  { bg: '#99F6E4', border: '#0D9488', text: '#134E4A' }, // 25. Dark Teal
  { bg: '#A5F3FC', border: '#0891B2', text: '#164E63' }, // 26. Dark Cyan
  { bg: '#FECDD3', border: '#E11D48', text: '#9F1239' }, // 27. Dark Rose
  { bg: '#D9F99D', border: '#65A30D', text: '#365314' }, // 28. Dark Lime
  { bg: '#FDE047', border: '#CA8A04', text: '#854D0E' }, // 29. Dark Yellow
  { bg: '#FDBA74', border: '#C2410C', text: '#7C2D12' }, // 30. Dark Orange
  // Additional unique hues
  { bg: '#E2E8F0', border: '#475569', text: '#1E293B' }, // 31. Slate
  { bg: '#F5F5F4', border: '#78716C', text: '#44403C' }, // 32. Stone
  { bg: '#FEF2F2', border: '#B91C1C', text: '#450A0A' }, // 33. Deep Red
  { bg: '#F0FDF4', border: '#15803D', text: '#14532D' }, // 34. Deep Green
  { bg: '#EFF6FF', border: '#1D4ED8', text: '#1E3A8A' }, // 35. Deep Blue
  { bg: '#FDF4FF', border: '#C026D3', text: '#701A75' }, // 36. Magenta
  { bg: '#FEFCE8', border: '#A16207', text: '#713F12' }, // 37. Gold
  { bg: '#F0FDFA', border: '#0F766E', text: '#134E4A' }, // 38. Sea Green
  { bg: '#FFF1F2', border: '#BE123C', text: '#881337' }, // 39. Crimson
  { bg: '#F5F3FF', border: '#6D28D9', text: '#4C1D95' }, // 40. Deep Violet
  // Pastel variants
  { bg: '#FFF7ED', border: '#FB923C', text: '#9A3412' }, // 41. Peach
  { bg: '#ECFDF5', border: '#34D399', text: '#065F46' }, // 42. Mint
  { bg: '#F0F9FF', border: '#38BDF8', text: '#0369A1' }, // 43. Light Sky
  { bg: '#FDF2F8', border: '#F472B6', text: '#9D174D' }, // 44. Light Pink
  { bg: '#EEF2FF', border: '#818CF8', text: '#3730A3' }, // 45. Lavender
  { bg: '#FFFBEB', border: '#FBBF24', text: '#92400E' }, // 46. Sunshine
  { bg: '#F7FEE7', border: '#A3E635', text: '#3F6212' }, // 47. Chartreuse
  { bg: '#FAFAF9', border: '#A8A29E', text: '#57534E' }, // 48. Warm Gray
  { bg: '#F8FAFC', border: '#94A3B8', text: '#334155' }, // 49. Cool Gray
  { bg: '#FEF7FF', border: '#E879F9', text: '#86198F' }, // 50. Orchid
];

// Destination colors (clouds, etc.)
const destinationColors: Record<string, { bg: string; border: string; icon: string }> = {
  'google cloud': { bg: '#E8F5E9', border: '#4CAF50', icon: '☁️' },
  'azure': { bg: '#E3F2FD', border: '#2196F3', icon: '☁️' },
  'aws': { bg: '#FFF3E0', border: '#FF9800', icon: '☁️' },
  'oracle cloud': { bg: '#FCE4EC', border: '#E91E63', icon: '☁️' },
  'internet': { bg: '#F3E5F5', border: '#9C27B0', icon: '🌐' },
  'default': { bg: '#ECEFF1', border: '#607D8B', icon: '📍' },
};

// Custom Node Component for Location (container with parents inside) - CENTER
const LocationNode = ({ data }: { data: { label: string; parents: Array<{ id: number; label: string; sku: string; category: string; capacity: number; serviceId: string | null; colorIndex: number }> } }) => {
  return (
    <div className="bg-white border-2 border-slate-300 rounded-xl shadow-lg p-4 min-w-[220px] relative">
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 pb-2 border-b border-slate-200">
        📍 {data.label}
      </div>
      <div className="space-y-2">
        {data.parents.map((parent) => {
          // Use unique color based on colorIndex
          const colors = uniqueParentColors[parent.colorIndex % uniqueParentColors.length];
          return (
            <div 
              key={parent.id}
              className="px-3 py-2 rounded-lg text-xs relative"
              style={{ backgroundColor: colors.bg, borderLeft: `4px solid ${colors.border}`, borderRight: `4px solid ${colors.border}` }}
            >
              {/* Left handle - aligned to this port box */}
              <Handle
                type="target"
                position={Position.Left}
                id={`left-parent-${parent.id}`}
                style={{ 
                  background: colors.border, 
                  width: 10, 
                  height: 10,
                  left: -22,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
              {/* Right handle - aligned to this port box */}
              <Handle
                type="source"
                position={Position.Right}
                id={`right-parent-${parent.id}`}
                style={{ 
                  background: colors.border, 
                  width: 10, 
                  height: 10,
                  right: -22,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
              <div className="font-semibold" style={{ color: colors.text }}>
                {parent.label}
              </div>
              <div className="font-mono text-[10px] mt-0.5" style={{ color: colors.text }}>
                {parent.sku}
              </div>
              {parent.capacity > 0 && (
                <div className="text-[10px] opacity-75 mt-0.5" style={{ color: colors.text }}>
                  {parent.capacity} Mbps
                </div>
              )}
            </div>
          );
        })}
        {data.parents.length === 0 && (
          <div className="text-xs text-slate-400 italic py-2">No infrastructure</div>
        )}
      </div>
    </div>
  );
};

// Custom Node for services on the RIGHT (Cloud Connectivity, Enterprise Internet)
const ServiceNodeRight = ({ data }: { data: { 
  sku: string; 
  capacity: number; 
  destination: string | null;
  parentColorIndex: number | null;
  category: string;
} }) => {
  // Get destination colors for the badge
  const destKey = data.destination?.toLowerCase() || '';
  const destColors = destinationColors[destKey] || destinationColors.default;
  
  // Get service border color based on parent assignment
  const serviceColors = data.parentColorIndex !== null 
    ? uniqueParentColors[data.parentColorIndex % uniqueParentColors.length]
    : { bg: '#F3F4F6', border: '#9CA3AF', text: '#6B7280' };

  return (
    <div 
      className="rounded-lg shadow-md p-2 min-w-[160px] relative bg-white"
      style={{ borderLeft: `4px solid ${serviceColors.border}` }}
    >
      {/* Left handle for incoming connections from center */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ background: serviceColors.border, width: 8, height: 8 }}
      />
      {/* Destination badge */}
      {data.destination && (
        <div 
          className="text-[9px] px-1.5 py-0.5 rounded-full mb-1 inline-flex items-center gap-1"
          style={{ backgroundColor: destColors.bg, color: destColors.border }}
        >
          <span>{destColors.icon}</span>
          <span className="font-medium">{data.destination}</span>
        </div>
      )}
      {!data.destination && data.category === 'enterprise_internet' && (
        <div className="text-[9px] px-1.5 py-0.5 rounded-full mb-1 inline-flex items-center gap-1 bg-purple-100 text-purple-600">
          <span>🌐</span>
          <span className="font-medium">Internet</span>
        </div>
      )}
      {/* SKU */}
      <div className="text-xs font-semibold text-slate-700 truncate" title={data.sku}>
        {data.sku}
      </div>
      {/* Capacity */}
      {data.capacity > 0 && (
        <div className="text-[10px] text-slate-500">
          {data.capacity} Mbps
        </div>
      )}
    </div>
  );
};

// Custom Node for Left-side services (Last Mile & CPE)
const ServiceNodeLeft = ({ data }: { data: { 
  sku: string; 
  capacity: number; 
  parentColorIndex: number | null;
  category?: string;
  categoryLabel?: string;
} }) => {
  // Get service border color based on parent assignment
  const serviceColors = data.parentColorIndex !== null 
    ? uniqueParentColors[data.parentColorIndex % uniqueParentColors.length]
    : { bg: '#F3F4F6', border: '#9CA3AF', text: '#6B7280' };

  // Badge styling based on category
  const isCPE = data.category === 'cpe';
  const badgeConfig = isCPE 
    ? { bg: 'bg-orange-100', text: 'text-orange-600', icon: '📦', label: 'CPE' }
    : { bg: 'bg-purple-100', text: 'text-purple-600', icon: '🔌', label: 'Last Mile' };

  return (
    <div 
      className="rounded-lg shadow-md p-2 min-w-[160px] relative bg-white"
      style={{ borderRight: `4px solid ${serviceColors.border}` }}
    >
      {/* Right handle for outgoing connections to center */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ background: serviceColors.border, width: 8, height: 8 }}
      />
      {/* Category badge (CPE or Last Mile) */}
      <div className={`text-[9px] px-1.5 py-0.5 rounded-full mb-1 inline-flex items-center gap-1 ${badgeConfig.bg} ${badgeConfig.text}`}>
        <span>{badgeConfig.icon}</span>
        <span className="font-medium">{badgeConfig.label}</span>
      </div>
      {/* SKU */}
      <div className="text-xs font-semibold text-slate-700 truncate" title={data.sku}>
        {data.sku}
      </div>
      {/* Capacity */}
      {data.capacity > 0 && (
        <div className="text-[10px] text-slate-500">
          {data.capacity} Mbps
        </div>
      )}
    </div>
  );
};

// Custom Node for DCI (Datacenter Interconnect) - positioned between two ports
const DCINode = ({ data }: { data: { 
  sku: string; 
  capacity: number;
  portASku: string;
  portBSku: string;
  portALocation: string;
  portBLocation: string;
  portAColorIndex: number;
  portBColorIndex: number;
} }) => {
  const dciColor = { bg: '#F3E8FF', border: '#A855F7', text: '#6B21A8' };
  
  // Get actual colors for each port based on their assigned color index
  const portAColors = uniqueParentColors[data.portAColorIndex % uniqueParentColors.length];
  const portBColors = uniqueParentColors[data.portBColorIndex % uniqueParentColors.length];

  return (
    <div 
      className="rounded-xl shadow-lg p-3 min-w-[220px] relative bg-white border-2"
      style={{ borderColor: dciColor.border }}
    >
      {/* DCI Header */}
      <div className="flex items-center justify-center gap-2 mb-2 pb-2 border-b border-purple-200">
        <span className="text-lg">🔗</span>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: dciColor.text }}>
          Datacenter Interconnect
        </span>
      </div>
      
      {/* SKU */}
      <div className="text-sm font-semibold text-center mb-2" style={{ color: dciColor.text }}>
        {data.sku}
      </div>
      
      {/* Capacity */}
      {data.capacity > 0 && (
        <div className="text-xs text-center text-purple-500 mb-3">
          {data.capacity} Mbps
        </div>
      )}
      
      {/* Port connections info - stacked vertically with handles aligned to each box */}
      <div className="space-y-2 text-[10px]">
        {/* Port A box with handle - uses actual port color */}
        <div 
          className="rounded p-1.5 flex items-center gap-2 relative"
          style={{ backgroundColor: portAColors.bg, borderLeft: `4px solid ${portAColors.border}` }}
        >
          <Handle
            type="target"
            position={Position.Left}
            id="port-a"
            style={{ 
              background: portAColors.border,
              width: 10, 
              height: 10,
              left: -18,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
          <div className="font-bold w-12" style={{ color: portAColors.text }}>Port A</div>
          <div className="flex-1">
            <div className="truncate" style={{ color: portAColors.text }} title={data.portASku}>{data.portASku}</div>
            <div className="truncate opacity-75" style={{ color: portAColors.text }} title={data.portALocation}>{data.portALocation}</div>
          </div>
        </div>
        {/* Port B box with handle - uses actual port color */}
        <div 
          className="rounded p-1.5 flex items-center gap-2 relative"
          style={{ backgroundColor: portBColors.bg, borderLeft: `4px solid ${portBColors.border}` }}
        >
          <Handle
            type="target"
            position={Position.Left}
            id="port-b"
            style={{ 
              background: portBColors.border,
              width: 10, 
              height: 10,
              left: -18,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
          <div className="font-bold w-12" style={{ color: portBColors.text }}>Port B</div>
          <div className="flex-1">
            <div className="truncate" style={{ color: portBColors.text }} title={data.portBSku}>{data.portBSku}</div>
            <div className="truncate opacity-75" style={{ color: portBColors.text }} title={data.portBLocation}>{data.portBLocation}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom Node for FWaaS (Intermediate - between Port and Services)
const FWaaSNode = ({ data }: { data: { 
  id: number;
  sku: string; 
  capacity: number;
  colorIndex: number;
} }) => {
  const colors = uniqueParentColors[data.colorIndex % uniqueParentColors.length];

  return (
    <div 
      className="rounded-xl shadow-lg p-3 min-w-[180px] relative bg-white border-2"
      style={{ borderColor: colors.border }}
    >
      {/* Left handle - receives from Ports */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ 
          background: colors.border, 
          width: 10, 
          height: 10,
        }}
      />
      {/* Right handle - sends to Services */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ 
          background: colors.border, 
          width: 10, 
          height: 10,
        }}
      />
      
      {/* FWaaS Header */}
      <div className="flex items-center justify-center gap-2 mb-2 pb-2 border-b" style={{ borderColor: colors.bg }}>
        <span className="text-base">🛡️</span>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: colors.text }}>
          Firewall
        </span>
      </div>
      
      {/* SKU */}
      <div 
        className="text-xs font-semibold text-center mb-1 truncate" 
        style={{ color: colors.text }}
        title={data.sku}
      >
        {data.sku}
      </div>
      
      {/* Capacity */}
      {data.capacity > 0 && (
        <div className="text-[10px] text-center" style={{ color: colors.text, opacity: 0.75 }}>
          {data.capacity} Mbps
        </div>
      )}
    </div>
  );
};

// Node types registration
const nodeTypes = {
  location: LocationNode,
  serviceRight: ServiceNodeRight,
  serviceLeft: ServiceNodeLeft,
  dci: DCINode,
  fwaas: FWaaSNode,
};

interface ServiceArchitectureGraphInnerProps extends ServiceArchitectureGraphProps {
  onExportReady?: (handle: ServiceArchitectureGraphHandle) => void;
}

function ServiceArchitectureGraphInner({ orderLines, assignments, parentDatacenterMap = {}, datacenters = [], dciPortAssignments = {}, sdwanHubRoles = {}, sdwanBranchPrimaryHub = {}, onExportReady }: ServiceArchitectureGraphInnerProps) {
  const { fitView, getNodes } = useReactFlow();
  const rfWrapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_exporting, setExporting] = useState(false);

  // Export function exposed via ref
  const exportDiagram = useCallback(async (format: 'svg' | 'png') => {
    const wrapper = rfWrapRef.current;
    const rfEl = wrapper?.querySelector('.react-flow') as HTMLElement | null;
    if (!rfEl) return;

    setExporting(true);
    try {
      const currentNodes = getNodes();
      if (currentNodes.length === 0) return;

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      currentNodes.forEach(node => {
        const x = node.position.x;
        const y = node.position.y;
        const w = node.width || 200;
        const h = node.height || 100;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + w);
        maxY = Math.max(maxY, y + h);
      });

      const PAD = 100;
      const width = Math.max(1200, Math.ceil(maxX - minX + PAD * 2));
      const height = Math.max(600, Math.ceil(maxY - minY + PAD * 2));

      const filterFn = (node: HTMLElement) => {
        const className = node.className || '';
        if (typeof className === 'string') {
          if (className.includes('react-flow__controls')) return false;
          if (className.includes('react-flow__minimap')) return false;
          if (className.includes('react-flow__attribution')) return false;
        }
        return true;
      };

      const commonOpts = {
        backgroundColor: '#f8fafc',
        filter: filterFn,
        width,
        height,
      };

      let blob: Blob;
      let filename: string;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      if (format === 'svg') {
        const dataUrl = await toSvg(rfEl, commonOpts);
        const svgText = await (await fetch(dataUrl)).text();
        blob = new Blob([svgText], { type: 'image/svg+xml' });
        filename = `presales-architecture-${timestamp}.svg`;
      } else {
        const MAX_DIM = 16384;
        const maxRatioW = Math.floor(MAX_DIM / width);
        const maxRatioH = Math.floor(MAX_DIM / height);
        const pixelRatio = Math.max(1, Math.min(4, maxRatioW, maxRatioH));
        const dataUrl = await toPng(rfEl, { ...commonOpts, pixelRatio });
        blob = await (await fetch(dataUrl)).blob();
        filename = `presales-architecture-${timestamp}.png`;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ServiceArchitectureGraph] Export error:', err);
    } finally {
      setExporting(false);
    }
  }, [getNodes]);

  // Get image as data URL (for Jira attachment)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getImageDataUrl = useCallback(async (_format: 'png'): Promise<string | null> => {
    const wrapper = rfWrapRef.current;
    const rfEl = wrapper?.querySelector('.react-flow') as HTMLElement | null;
    if (!rfEl) return null;

    try {
      const currentNodes = getNodes();
      if (currentNodes.length === 0) return null;

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      currentNodes.forEach(node => {
        const x = node.position.x;
        const y = node.position.y;
        const w = node.width || 200;
        const h = node.height || 100;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + w);
        maxY = Math.max(maxY, y + h);
      });

      const PAD = 100;
      const width = Math.max(1200, Math.ceil(maxX - minX + PAD * 2));
      const height = Math.max(600, Math.ceil(maxY - minY + PAD * 2));

      const filterFn = (node: HTMLElement) => {
        const className = node.className || '';
        if (typeof className === 'string') {
          if (className.includes('react-flow__controls')) return false;
          if (className.includes('react-flow__minimap')) return false;
          if (className.includes('react-flow__attribution')) return false;
        }
        return true;
      };

      const commonOpts = {
        backgroundColor: '#f8fafc',
        filter: filterFn,
        width,
        height,
      };

      const MAX_DIM = 16384;
      const maxRatioW = Math.floor(MAX_DIM / width);
      const maxRatioH = Math.floor(MAX_DIM / height);
      const pixelRatio = Math.max(1, Math.min(2, maxRatioW, maxRatioH)); // Lower ratio for Jira
      const dataUrl = await toPng(rfEl, { ...commonOpts, pixelRatio });
      
      return dataUrl;
    } catch (err) {
      console.error('[ServiceArchitectureGraph] getImageDataUrl error:', err);
      return null;
    }
  }, [getNodes]);

  // Expose export functions via callback
  useEffect(() => {
    if (onExportReady) {
      onExportReady({
        exportDiagram,
        getImageDataUrl,
      });
    }
  }, [onExportReady, exportDiagram, getImageDataUrl]);

  // Generate nodes and edges based on assignments
  const { initialNodes, initialEdges, allParents, dciConnections } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Filter out NRC products first (non-recurring charges)
    const assignableLines = filterAssignableLines(orderLines);

    // Assign unique color index to each parent instance
    const parentColorMap = new Map<number, number>();
    let colorIndex = 0;

    // Group parents by SELECTED DATACENTER (not Odoo origin)
    // Note: DCI is NOT included here - it's rendered as an edge connecting two ports
    const locationGroups = new Map<string, { 
      location: { id: string; name: string } | null; 
      parents: OdooOrderLine[];
    }>();

    // Collect all children (excluding NRC products)
    const children = assignableLines.filter(line => CHILD_CATEGORIES.includes(line.product.category));
    
    // Collect DCI lines (they connect two ports)
    const dciLines = assignableLines.filter(line => line.product.category === DCI_CATEGORY);

    // Create a map of parent ID to parent for quick lookup
    const parentsMap = new Map<number, OdooOrderLine>();
    
    // Collect SD-WAN Hubs separately (they are standalone, not grouped by datacenter)
    const sdwanHubs: OdooOrderLine[] = [];

    // Group parents by SELECTED datacenter and assign unique colors
    // EXCLUDE DCI from parents - they are connectors, not infrastructure
    assignableLines.forEach(line => {
      if (PARENT_CATEGORIES.includes(line.product.category)) {
        parentsMap.set(line.id, line);
        parentColorMap.set(line.id, colorIndex);
        colorIndex++;

        // SD-WAN Hubs are standalone nodes (no datacenter grouping)
        if (line.product.category === 'sdwan_hub') {
          sdwanHubs.push(line);
          return;
        }

        // Use selected datacenter if available, otherwise use 'unassigned'
        const selectedDC = parentDatacenterMap[line.id];
        const locationKey = selectedDC || 'unassigned';

        // Get datacenter name from datacenters list
        const dcInfo = selectedDC
          ? datacenters.find(dc => dc.datacenterId === selectedDC)
          : null;

        if (!locationGroups.has(locationKey)) {
          locationGroups.set(locationKey, {
            location: dcInfo ? { id: dcInfo.datacenterId, name: dcInfo.name } : null,
            parents: []
          });
        }
        const group = locationGroups.get(locationKey);
        if (group) {
          group.parents.push(line);
        }
      }
    });

    // Separate children by type:
    // - Left (Column 1): Last Mile, CPE
    // - FWaaS (Column 3): Intermediate firewall services
    // - Right (Column 4): Cloud Connectivity, Enterprise Internet
    const leftServices = children.filter(c => LEFT_SIDE_CATEGORIES.includes(c.product.category));
    const fwaasServices = children.filter(c => c.product.category === FWAAS_CATEGORY);
    const rightServices = children.filter(c => RIGHT_SIDE_CATEGORIES.includes(c.product.category));
    // SD-WAN Branches are separate (connect to all hubs, not just one parent)
    const sdwanBranchServices = assignableLines.filter(c => c.product.category === 'sdwan_branch');
    
    // Check if we have intermediate services (FWaaS or DCI) to determine layout
    const hasFwaas = fwaasServices.length > 0;
    const hasDci = dciLines.length > 0;
    const hasIntermediateColumn = hasFwaas || hasDci;

    // Position calculations - 4 columns layout
    // Column 1: Last Mile & CPE (left side)
    // Column 2: Infrastructure (Ports in location boxes)
    // Column 3: Intermediate (FWaaS & DCI) - only if exists
    // Column 4: Cloud/Internet services (right side)
    const leftColumnX = 50;         // Column 1: Last Mile & CPE
    const portColumnX = 350;        // Column 2: Infrastructure (Ports)
    const intermediateColumnX = hasIntermediateColumn ? 650 : 0;  // Column 3: FWaaS & DCI
    const rightColumnX = hasIntermediateColumn ? 950 : 600; // Column 4: Cloud/Internet services
    const serviceSpacingY = 120;   // Vertical spacing between services - increased from 80
    const locationSpacingY = 400;  // Vertical spacing between location groups - increased from 300
    
    // Assign unique color index to FWaaS instances
    fwaasServices.forEach(fwaas => {
      parentColorMap.set(fwaas.id, colorIndex);
      colorIndex++;
    });

    // Create location nodes (Column 2 - Ports/Infrastructure)
    let locationIndex = 0;
    locationGroups.forEach((group, locationKey) => {
      const nodeId = `location-${locationKey}`;
      nodes.push({
        id: nodeId,
        type: 'location',
        position: { x: portColumnX, y: locationIndex * locationSpacingY },
        data: {
          label: group.location?.name || 'Unknown Location',
          parents: group.parents.map(p => ({
            id: p.id,
            label: p.product.categoryLabel,
            sku: p.product.sku || p.product.name,
            category: p.product.category,
            capacity: p.capacity,
            serviceId: p.serviceId,
            colorIndex: parentColorMap.get(p.id) ?? 0,
          })),
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
      locationIndex++;
    });

    // Create SD-WAN Hub standalone nodes (Column 2, below location groups)
    sdwanHubs.forEach((hub) => {
      const hubNodeId = `sdwan-hub-${hub.id}`;
      const role = sdwanHubRoles[hub.id];
      const hubColorIndex = parentColorMap.get(hub.id) ?? 0;
      const hubColors = uniqueParentColors[hubColorIndex % uniqueParentColors.length];

      nodes.push({
        id: hubNodeId,
        type: 'location',
        position: { x: portColumnX, y: locationIndex * locationSpacingY },
        data: {
          label: role
            ? `SD-WAN Hub (${role === 'primary' ? 'Primary' : 'Secondary'}) - ${hub.origin?.name || 'N/A'}`
            : `SD-WAN Hub - ${hub.origin?.name || 'N/A'}`,
          parents: [{
            id: hub.id,
            label: hub.product.categoryLabel || 'SD-WAN Hub',
            sku: hub.product.sku || hub.product.name,
            category: hub.product.category,
            capacity: hub.capacity,
            serviceId: hub.serviceId,
            colorIndex: hubColorIndex,
          }],
          borderColor: hubColors.border,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
      locationIndex++;
    });

    // Create LEFT side service nodes (Last Mile & CPE)
    let leftServiceIndex = 0;
    leftServices.forEach(child => {
      const assignment = assignments.find(a => a.childLineIds.includes(child.id));
      const parentId = assignment?.parentLineId || null;
      
      let locationKey = 'unassigned';
      let parentColorIndex: number | null = null;
      
      if (parentId) {
        // Use the SELECTED datacenter for the parent
        const selectedDC = parentDatacenterMap[parentId];
        locationKey = selectedDC || 'unassigned';
        parentColorIndex = parentColorMap.get(parentId) ?? null;
      }

      const serviceNodeId = `left-${child.id}`;
      
      nodes.push({
        id: serviceNodeId,
        type: 'serviceLeft',
        position: { x: leftColumnX, y: leftServiceIndex * serviceSpacingY },
        data: {
          sku: child.product.sku || child.product.name,
          capacity: child.capacity,
          parentColorIndex,
          category: child.product.category, // Pass category to show CPE vs Last Mile
          categoryLabel: child.product.categoryLabel,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Right,
      });

      // Create edge from Left service to specific Parent (left to center) - ONLY if assigned
      if (parentId !== null && parentColorIndex !== null && locationGroups.has(locationKey)) {
        const targetId = `location-${locationKey}`;
        const parentColors = uniqueParentColors[parentColorIndex % uniqueParentColors.length];
        edges.push({
          id: `edge-left-${child.id}`,
          source: serviceNodeId,
          sourceHandle: 'right',
          target: targetId,
          targetHandle: `left-parent-${parentId}`, // Connect to specific parent handle
          type: 'default',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 10,
            height: 10,
            color: parentColors.border,
          },
          style: { 
            stroke: parentColors.border, 
            strokeWidth: 2,
            strokeDasharray: '8 4',
          },
          animated: true,
        });
      }

      leftServiceIndex++;
    });

    // Create FWaaS nodes (Column 3 - INTERMEDIATE, only if exists)
    // FWaaS can receive from Ports and send to Services
    const fwaasMap = new Map<number, { nodeId: string; colorIndex: number }>();
    if (hasFwaas) {
      let fwaasIndex = 0;
      fwaasServices.forEach(fwaas => {
        const fwaasNodeId = `fwaas-${fwaas.id}`;
        const fwaasColorIndex = parentColorMap.get(fwaas.id) ?? 0;
        
        fwaasMap.set(fwaas.id, { nodeId: fwaasNodeId, colorIndex: fwaasColorIndex });
        
        // Check if FWaaS is assigned to a Port
        const fwaasAssignment = assignments.find(a => a.childLineIds.includes(fwaas.id));
        const assignedPortId = fwaasAssignment?.parentLineId || null;
        let assignedPortDC: string | null = null;
        if (assignedPortId && parentDatacenterMap[assignedPortId]) {
          assignedPortDC = parentDatacenterMap[assignedPortId];
        }
        
        nodes.push({
          id: fwaasNodeId,
          type: 'fwaas',
          position: { x: intermediateColumnX, y: fwaasIndex * serviceSpacingY + 80 },
          data: {
            id: fwaas.id,
            sku: fwaas.product.sku || fwaas.product.name,
            capacity: fwaas.capacity,
            colorIndex: fwaasColorIndex,
          },
        });
        
        // Create edge from Port to FWaaS if FWaaS is assigned to a Port
        if (assignedPortId && assignedPortDC && locationGroups.has(assignedPortDC)) {
          const portColorIndex = parentColorMap.get(assignedPortId) ?? 0;
          const portColors = uniqueParentColors[portColorIndex % uniqueParentColors.length];
          
          edges.push({
            id: `edge-port-fwaas-${fwaas.id}`,
            source: `location-${assignedPortDC}`,
            sourceHandle: `right-parent-${assignedPortId}`,
            target: fwaasNodeId,
            targetHandle: 'left',
            type: 'default',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: portColors.border,
            },
            style: { 
              stroke: portColors.border, 
              strokeWidth: 2,
              strokeDasharray: '8 4',
            },
            animated: true,
          });
        }
        
        fwaasIndex++;
      });
    }

    // Create Cloud/Internet service nodes (Column 4 - RIGHT side)
    let rightServiceIndex = 0;
    rightServices.forEach(child => {
      const assignment = assignments.find(a => a.childLineIds.includes(child.id));
      const parentId = assignment?.parentLineId || null;
      
      // Check if assigned to a FWaaS, SD-WAN Hub, or a Port
      const assignedToFwaas = parentId ? fwaasMap.has(parentId) : false;
      const fwaasInfo = parentId ? fwaasMap.get(parentId) : null;
      const assignedToSDWANHub = parentId ? sdwanHubs.some(h => h.id === parentId) : false;

      let locationKey = 'unassigned';
      let parentColorIndex: number | null = null;

      if (parentId) {
        if (assignedToFwaas && fwaasInfo) {
          // Assigned to FWaaS
          parentColorIndex = fwaasInfo.colorIndex;
        } else if (assignedToSDWANHub) {
          // Assigned to SD-WAN Hub
          parentColorIndex = parentColorMap.get(parentId) ?? null;
        } else {
          // Assigned to Port
          const selectedDC = parentDatacenterMap[parentId];
          locationKey = selectedDC || 'unassigned';
          parentColorIndex = parentColorMap.get(parentId) ?? null;
        }
      }

      const serviceNodeId = `service-${child.id}`;
      
      nodes.push({
        id: serviceNodeId,
        type: 'serviceRight',
        position: { x: rightColumnX, y: rightServiceIndex * serviceSpacingY },
        data: {
          sku: child.product.sku || child.product.name,
          capacity: child.capacity,
          destination: child.destination?.name || null,
          parentColorIndex,
          category: child.product.category,
        },
        sourcePosition: Position.Left,
        targetPosition: Position.Left,
      });

      // Create edge - from FWaaS or from Port depending on assignment
      if (parentId !== null && parentColorIndex !== null) {
        if (assignedToFwaas && fwaasInfo) {
          // Edge from FWaaS to Service
          const fwaasColors = uniqueParentColors[fwaasInfo.colorIndex % uniqueParentColors.length];
          edges.push({
            id: `edge-fwaas-svc-${child.id}`,
            source: fwaasInfo.nodeId,
            sourceHandle: 'right',
            target: serviceNodeId,
            targetHandle: 'left',
            type: 'default',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: fwaasColors.border,
            },
            style: { 
              stroke: fwaasColors.border, 
              strokeWidth: 2,
              strokeDasharray: '8 4',
            },
            animated: true,
          });
        } else if (assignedToSDWANHub && parentId) {
          // Edge from SD-WAN Hub to Service
          const hubNodeId = `sdwan-hub-${parentId}`;
          const hubColors = uniqueParentColors[parentColorIndex % uniqueParentColors.length];
          edges.push({
            id: `edge-hub-svc-${child.id}`,
            source: hubNodeId,
            sourceHandle: `right-parent-${parentId}`,
            target: serviceNodeId,
            targetHandle: 'left',
            type: 'default',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: hubColors.border,
            },
            style: {
              stroke: hubColors.border,
              strokeWidth: 2,
              strokeDasharray: '8 4',
            },
            animated: true,
          });
        } else if (locationGroups.has(locationKey)) {
          // Edge from Port to Service (direct, no FWaaS)
          const sourceId = `location-${locationKey}`;
          const parentColors = uniqueParentColors[parentColorIndex % uniqueParentColors.length];
          edges.push({
            id: `edge-${child.id}`,
            source: sourceId,
            sourceHandle: `right-parent-${parentId}`,
            target: serviceNodeId,
            targetHandle: 'left',
            type: 'default',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: parentColors.border,
            },
            style: { 
              stroke: parentColors.border, 
              strokeWidth: 2,
              strokeDasharray: '8 4',
            },
            animated: true,
          });
        }
      }

      rightServiceIndex++;
    });

    // Note: Port → FWaaS edges are created inside the FWaaS node creation loop above

    // Create SD-WAN Branch nodes and edges to ALL hubs
    sdwanBranchServices.forEach(branch => {
      const branchNodeId = `branch-${branch.id}`;
      const primaryHubId = sdwanBranchPrimaryHub[branch.id];

      nodes.push({
        id: branchNodeId,
        type: 'serviceRight',
        position: { x: rightColumnX, y: rightServiceIndex * serviceSpacingY },
        data: {
          sku: branch.product.sku || branch.product.name,
          capacity: branch.capacity,
          destination: branch.quantity > 1 ? `x${branch.quantity}` : null,
          parentColorIndex: primaryHubId ? (parentColorMap.get(primaryHubId) ?? null) : null,
          category: branch.product.category,
        },
        sourcePosition: Position.Left,
        targetPosition: Position.Left,
      });

      // Create edges to ALL hubs (primary = solid, secondary = dashed)
      if (primaryHubId) {
        sdwanHubs.forEach(hub => {
          const isPrimary = hub.id === primaryHubId;
          const hubColorIndex = parentColorMap.get(hub.id) ?? 0;
          const hubColors = uniqueParentColors[hubColorIndex % uniqueParentColors.length];

          edges.push({
            id: `edge-branch-hub-${branch.id}-${hub.id}`,
            source: `sdwan-hub-${hub.id}`,
            sourceHandle: `right-parent-${hub.id}`,
            target: branchNodeId,
            targetHandle: 'left',
            type: 'default',
            label: isPrimary ? 'P' : 'S',
            labelStyle: { fontSize: 9, fontWeight: 700, fill: hubColors.text },
            labelBgStyle: { fill: hubColors.bg, fillOpacity: 0.9 },
            labelBgPadding: [4, 2] as [number, number],
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 8,
              height: 8,
              color: hubColors.border,
            },
            style: {
              stroke: hubColors.border,
              strokeWidth: isPrimary ? 2.5 : 1.5,
              strokeDasharray: isPrimary ? undefined : '6 4',
            },
            animated: isPrimary,
          });
        });
      }

      rightServiceIndex++;
    });

    // Get all infrastructure items for legend (Ports + FWaaS)
    const allParents: Array<{ id: number; sku: string; label: string; colorIndex: number }> = [];
    
    // Add Ports (excluding NRC products)
    assignableLines.forEach(line => {
      if (PARENT_CATEGORIES.includes(line.product.category)) {
        const ci = parentColorMap.get(line.id);
        if (ci !== undefined) {
          allParents.push({
            id: line.id,
            sku: line.product.sku || line.product.name,
            label: line.product.categoryLabel,
            colorIndex: ci,
          });
        }
      }
    });
    
    // Add FWaaS
    fwaasServices.forEach(fwaas => {
      const ci = parentColorMap.get(fwaas.id);
      if (ci !== undefined) {
        allParents.push({
          id: fwaas.id,
          sku: fwaas.product.sku || fwaas.product.name,
          label: '🛡️ FWaaS',
          colorIndex: ci,
        });
      }
    });

    // DCI connections info for legend
    const dciConnections: Array<{ id: number; sku: string; portA: string; portB: string }> = [];

    // Create DCI nodes and edges connecting to both ports
    let dciIndex = 0;
    dciLines.forEach(dci => {
      const dciAssignment = dciPortAssignments[dci.id];
      if (dciAssignment?.portA && dciAssignment?.portB) {
        const portA = parentsMap.get(dciAssignment.portA);
        const portB = parentsMap.get(dciAssignment.portB);
        
        if (portA && portB) {
          // Get the location keys for both ports
          const selectedDCA = parentDatacenterMap[portA.id];
          const locationKeyA = selectedDCA || 'unassigned';
          const locationNameA = selectedDCA 
            ? datacenters.find(dc => dc.datacenterId === selectedDCA)?.name || 'Unknown'
            : portA.origin?.name || 'Unknown';
          
          const selectedDCB = parentDatacenterMap[portB.id];
          const locationKeyB = selectedDCB || 'unassigned';
          const locationNameB = selectedDCB 
            ? datacenters.find(dc => dc.datacenterId === selectedDCB)?.name || 'Unknown'
            : portB.origin?.name || 'Unknown';
          
          // Get actual color indices for Port A and Port B
          const portAColorIndex = parentColorMap.get(portA.id) ?? 0;
          const portBColorIndex = parentColorMap.get(portB.id) ?? 1;
          const portAColors = uniqueParentColors[portAColorIndex % uniqueParentColors.length];
          const portBColors = uniqueParentColors[portBColorIndex % uniqueParentColors.length];
          
          // Create DCI node - positioned in intermediate column (Column 3) below FWaaS
          const dciNodeId = `dci-${dci.id}`;
          const dciYOffset = fwaasServices.length * serviceSpacingY + 120; // Position below FWaaS nodes
          nodes.push({
            id: dciNodeId,
            type: 'dci',
            position: { x: intermediateColumnX, y: dciYOffset + dciIndex * 280 },
            data: {
              sku: dci.product.sku || dci.product.name,
              capacity: dci.capacity,
              portASku: portA.product.sku || portA.product.name,
              portBSku: portB.product.sku || portB.product.name,
              portALocation: locationNameA,
              portBLocation: locationNameB,
              portAColorIndex: portAColorIndex,
              portBColorIndex: portBColorIndex,
            },
          });
          
          // Edge from Port A to DCI - uses Port A's actual color
          edges.push({
            id: `edge-dci-${dci.id}-portA`,
            source: `location-${locationKeyA}`,
            sourceHandle: `right-parent-${portA.id}`,
            target: dciNodeId,
            targetHandle: 'port-a',
            type: 'default',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: portAColors.border,
            },
            style: { 
              stroke: portAColors.border,
              strokeWidth: 2,
              strokeDasharray: '8 4',
            },
            animated: true,
          });
          
          // Edge from Port B to DCI - uses Port B's actual color
          edges.push({
            id: `edge-dci-${dci.id}-portB`,
            source: `location-${locationKeyB}`,
            sourceHandle: `right-parent-${portB.id}`,
            target: dciNodeId,
            targetHandle: 'port-b',
            type: 'default',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: portBColors.border,
            },
            style: { 
              stroke: portBColors.border,
              strokeWidth: 2,
              strokeDasharray: '8 4',
            },
            animated: true,
          });
          
          // Add to DCI connections for legend
          dciConnections.push({
            id: dci.id,
            sku: dci.product.sku || dci.product.name,
            portA: portA.product.sku || portA.serviceId || String(portA.id),
            portB: portB.product.sku || portB.serviceId || String(portB.id),
          });
          
          dciIndex++;
        }
      }
    });

    return { initialNodes: nodes, initialEdges: edges, allParents, dciConnections };
  }, [orderLines, assignments, parentDatacenterMap, datacenters, dciPortAssignments, sdwanHubRoles, sdwanBranchPrimaryHub]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Update nodes/edges when data changes - use JSON stringify to detect deep changes
  React.useEffect(() => {
    setNodes([...initialNodes]);
    setEdges([...initialEdges]);
    // Small delay to ensure DOM is updated before fitting view
    const timer = setTimeout(() => fitView({ padding: 0.2 }), 150);
    return () => clearTimeout(timer);
  }, [JSON.stringify(initialNodes), JSON.stringify(initialEdges), setNodes, setEdges, fitView]);

  const onInit = useCallback(() => {
    setTimeout(() => fitView({ padding: 0.2 }), 100);
  }, [fitView]);

  // Auto-layout function - reorganizes nodes in a clean 4-column grid
  const handleAutoLayout = useCallback(() => {
    const leftColumnX = 50;
    const portColumnX = 350;
    const intermediateColumnX = 700;
    const rightColumnX = 1050;
    const verticalSpacing = 150;
    const locationSpacing = 300;
    
    const locationNodes = nodes.filter(n => n.type === 'location');
    const leftNodes = nodes.filter(n => n.type === 'serviceLeft');
    const rightNodes = nodes.filter(n => n.type === 'serviceRight');
    const fwaasNodes = nodes.filter(n => n.type === 'fwaas');
    const dciNodes = nodes.filter(n => n.type === 'dci');
    
    const locationTotalHeight = (locationNodes.length - 1) * locationSpacing;
    const startY = 50;
    
    const newNodes = nodes.map(node => {
      if (node.type === 'serviceLeft') {
        const index = leftNodes.indexOf(node);
        return { ...node, position: { x: leftColumnX, y: startY + index * verticalSpacing } };
      }
      if (node.type === 'location') {
        const index = locationNodes.indexOf(node);
        return { ...node, position: { x: portColumnX, y: startY + index * locationSpacing } };
      }
      if (node.type === 'fwaas') {
        const index = fwaasNodes.indexOf(node);
        return { ...node, position: { x: intermediateColumnX, y: startY + 50 + index * verticalSpacing } };
      }
      if (node.type === 'dci') {
        const index = dciNodes.indexOf(node);
        const dciStartY = startY + (locationTotalHeight / 2) - 50;
        return { ...node, position: { x: intermediateColumnX, y: dciStartY + index * 350 } };
      }
      if (node.type === 'serviceRight') {
        const index = rightNodes.indexOf(node);
        return { ...node, position: { x: rightColumnX, y: startY + index * verticalSpacing } };
      }
      return node;
    });
    
    setNodes(newNodes);
    setTimeout(() => fitView({ padding: 0.15 }), 100);
  }, [nodes, setNodes, fitView]);

  if (initialNodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-semantic-text-secondary">
        No services to display in the architecture diagram.
      </div>
    );
  }

  return (
    <div ref={rfWrapRef} className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onInit={onInit}
        fitView
        attributionPosition="bottom-left"
        minZoom={0.3}
        maxZoom={1.5}
        className="bg-gradient-to-br from-slate-50 to-slate-100"
      >
      {/* Controls */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button
          onClick={handleAutoLayout}
          className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg shadow-sm text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center gap-1.5"
        >
          <span>📐</span>
          Auto Layout
        </button>
        <button
          onClick={() => fitView({ padding: 0.15 })}
          className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg shadow-sm text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center gap-1.5"
        >
          <span>🔍</span>
          Fit View
        </button>
      </div>
      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-md border border-slate-200 z-10 max-w-[250px]">
        {/* Layout explanation */}
        <div className="text-[10px] text-slate-400 mb-2 pb-2 border-b border-slate-100">
          🔌 Last Mile → 📍 Infrastructure → ☁️ Cloud/Internet
        </div>
        
        <div className="text-xs font-semibold text-slate-500 mb-2">Infrastructure Colors</div>
        <div className="space-y-1.5 text-xs max-h-[120px] overflow-y-auto">
          {allParents.map((parent) => {
            const colors = uniqueParentColors[parent.colorIndex % uniqueParentColors.length];
            return (
              <div key={parent.id} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded flex-shrink-0" 
                  style={{ backgroundColor: colors.bg, border: `2px solid ${colors.border}` }} 
                />
                <span 
                  className="truncate" 
                  style={{ color: colors.text }}
                  title={parent.sku}
                >
                  {parent.sku.substring(0, 16)}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* DCI Connections */}
        {dciConnections.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold text-purple-600 mb-1.5">DCI Connections</div>
            <div className="space-y-1 text-xs">
              {dciConnections.map((dci) => (
                <div key={dci.id} className="flex items-center gap-1.5">
                  <div 
                    className="w-6 h-0.5 flex-shrink-0" 
                    style={{ backgroundColor: '#A855F7', borderTop: '2px dashed #A855F7' }} 
                  />
                  <span className="truncate text-purple-700" title={`${dci.portA} ↔ ${dci.portB}`}>
                    {dci.portA.substring(0, 8)} ↔ {dci.portB.substring(0, 8)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
          Lines appear when services are assigned
        </div>
      </div>
      </ReactFlow>
    </div>
  );
}

// Wrapper component with forwardRef support
const ServiceArchitectureGraph = forwardRef<ServiceArchitectureGraphHandle, ServiceArchitectureGraphProps>(
  function ServiceArchitectureGraph(props, ref) {
    const handleRef = useRef<ServiceArchitectureGraphHandle | null>(null);
    
    // Expose the handle via the forwarded ref
    useImperativeHandle(ref, () => ({
      exportDiagram: async (format: 'svg' | 'png') => {
        if (handleRef.current) {
          await handleRef.current.exportDiagram(format);
        }
      },
      getImageDataUrl: async (format: 'png') => {
        if (handleRef.current) {
          return handleRef.current.getImageDataUrl(format);
        }
        return null;
      },
    }), []);

    return (
      <ReactFlowProvider>
        <ServiceArchitectureGraphInner 
          {...props} 
          onExportReady={(handle) => {
            handleRef.current = handle;
          }}
        />
      </ReactFlowProvider>
    );
  }
);

export default ServiceArchitectureGraph;
